'use client';

import * as React from 'react';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { Slot } from '@radix-ui/react-slot';

interface QuantityContextValue {
  quantity: number;
  onDecrement: () => void;
  onIncrement: () => void;
  min: number;
  max: number;
}

const QuantityContext = React.createContext<QuantityContextValue | undefined>(undefined);

function useQuantity() {
  const context = React.useContext(QuantityContext);
  if (!context) {
    throw new Error('useQuantity must be used within a Quantity component');
  }
  return context;
}

interface QuantityProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: number;
  min?: number;
  max?: number;
  onValueChange?: (value: number) => void;
  children?: React.ReactNode;
}

function Quantity({
  className,
  value = 0,
  min = 0,
  max = 99,
  onValueChange,
  children,
  ...props
}: QuantityProps) {
  const handleDecrement = () => {
    if (value > min) {
      onValueChange?.(value - 1);
    }
  };

  const handleIncrement = () => {
    if (value < max) {
      onValueChange?.(value + 1);
    }
  };

  const contextValue: QuantityContextValue = {
    quantity: value,
    onDecrement: handleDecrement,
    onIncrement: handleIncrement,
    min,
    max,
  };

  if (children) {
    return (
      <QuantityContext.Provider value={contextValue}>
        <Slot data-slot="quantity" className={cn('flex items-center gap-2', className)} {...props}>
          {children}
        </Slot>
      </QuantityContext.Provider>
    );
  }

  return (
    <QuantityContext.Provider value={contextValue}>
      <div
        data-slot="quantity"
        className={cn('flex items-center min-w-[4rem]', className)}
        {...props}
      >
        <QuantityDecrement />
        <QuantityValue />
        <QuantityIncrement />
      </div>
    </QuantityContext.Provider>
  );
}

function QuantityDecrement({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { onDecrement, min, quantity } = useQuantity();

  if (quantity === 0) return null;

  return (
    <button
      data-slot="quantity-decrement"
      onClick={onDecrement}
      disabled={quantity <= min}
      className={cn(
        'w-5 h-5 flex-1 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
      {...props}
    >
      <MinusIcon className={cn('w-5 h-5 text-night')} />
    </button>
  );
}

function QuantityValue({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  const { quantity } = useQuantity();

  if (quantity === 0) return null;

  return (
    <span
      data-slot="quantity-input"
      className={cn('text-sm flex-1 font-medium text-center', className)}
      {...props}
    >
      {quantity}
    </span>
  );
}

function QuantityIncrement({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { onIncrement, max, quantity } = useQuantity();

  return (
    <button
      data-slot="quantity-increment"
      onClick={onIncrement}
      disabled={quantity >= max}
      className={cn(
        'w-5 h-5 flex-1 rounded-full flex items-center justify-end transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
      {...props}
    >
      <PlusIcon className={cn('w-5 h-5 text-primary')} />
    </button>
  );
}

export { Quantity, QuantityDecrement, QuantityValue, QuantityIncrement };
